import { type Fields, RecordKey } from "./records";

/**
 * A version's records, as a draft from it begins with them: everything its draft held when it was
 * published, but its page, which the version's own row says.
 */
export const DRAFT_RECORDS_FILE = "records.ndjson";
export const DRAFT_RECORDS_CONTENT_TYPE = "application/x-ndjson";

const FORMAT = "pax-draft-records";

/** Records as a file: a header line, then each record as `[key, fields]` on a line of its own, in order. */
export function encodeDraftRecords(records: Iterable<readonly [string, Fields]>): string {
  const lines = [JSON.stringify({ format: FORMAT })];
  for (const [key, fields] of records) lines.push(JSON.stringify([key, fields]));
  return `${lines.join("\n")}\n`;
}

/**
 * Each record a file holds, in its order, read a line at a time, so a large file is never all in
 * memory as records at once. Throws, once it reaches it, at whatever isn't a records file.
 */
export function* draftRecordsIn(text: string): Generator<[string, Fields]> {
  if (!text.endsWith("\n")) throw new Error("records file has an incomplete final line");
  let at = text.indexOf("\n");
  const header = text.slice(0, at);
  const format = header.startsWith("{")
    ? (JSON.parse(header) as { format?: unknown }).format
    : null;
  if (format !== FORMAT) throw new Error(`records file does not start with a ${FORMAT} header`);
  for (let line = 2; at < text.length - 1; line++) {
    const end = text.indexOf("\n", at + 1);
    const parsed = JSON.parse(text.slice(at + 1, end)) as unknown;
    at = end;
    if (
      !Array.isArray(parsed) ||
      parsed.length !== 2 ||
      !RecordKey.safeParse(parsed[0]).success ||
      typeof parsed[1] !== "object" ||
      parsed[1] === null ||
      Array.isArray(parsed[1])
    ) {
      throw new Error(`records file line ${line} isn't a record`);
    }
    yield parsed as [string, Fields];
  }
}

/** The records a file holds, in its order. Throws when it isn't a records file. */
export function parseDraftRecords(text: string): Map<string, Fields> {
  const records = new Map<string, Fields>();
  for (const [key, fields] of draftRecordsIn(text)) {
    if (records.has(key)) throw new Error(`records file holds ${key} twice`);
    records.set(key, fields);
  }
  return records;
}
